import { useCallback, useRef, useState } from "react";

const DEFAULT_SIDEBAR_PERCENT = 50;
const MAX_SIDEBAR_PERCENT = 55;
const MIN_SIDEBAR_PX = 280;
const MIN_LIST_PX = 400;

/**
 * Clamp sidebar width percent for a given layout width.
 *
 * @example
 * // layoutWidth 1000, percent 30 → 30
 * // layoutWidth 1000, percent 5 → ~28 (280px min sidebar)
 */
const clampSidebarPercent = (percent, layoutWidth) => {
  const minPercent = (MIN_SIDEBAR_PX / layoutWidth) * 100;
  const maxPercent = Math.min(
    MAX_SIDEBAR_PERCENT,
    100 - (MIN_LIST_PX / layoutWidth) * 100
  );
  return Math.min(maxPercent, Math.max(minPercent, percent));
};

/**
 * Drag-to-resize state for the global action items list / details split.
 *
 * @param {boolean} enabled - True when the docked details sidebar is visible.
 * @returns {{ layoutRef: import('react').RefObject<HTMLDivElement|null>, splitStyle: object, isResizing: boolean, onDividerPointerDown: (event: import('react').PointerEvent) => void }}
 *
 * @example
 * const { layoutRef, splitStyle, onDividerPointerDown } = useActionItemsSplitResize(true);
 * // splitStyle → { '--action-items-split-list': '50%', '--action-items-split-sidebar': '50%' }
 */
export const useActionItemsSplitResize = (enabled) => {
  const layoutRef = useRef(null);
  const [sidebarPercent, setSidebarPercent] = useState(DEFAULT_SIDEBAR_PERCENT);
  const [isResizing, setIsResizing] = useState(false);
  const dragRef = useRef(null);

  const onDividerPointerDown = useCallback(
    (event) => {
      if (!enabled || event.button !== 0) return;
      event.preventDefault();
      const layout = layoutRef.current;
      if (!layout) return;

      const { width } = layout.getBoundingClientRect();
      dragRef.current = {
        startX: event.clientX,
        startPercent: sidebarPercent,
        width,
      };
      setIsResizing(true);
      const dividerEl = event.currentTarget;
      dividerEl.setPointerCapture(event.pointerId);

      const onPointerMove = (moveEvent) => {
        const drag = dragRef.current;
        if (!drag) return;
        const deltaX = moveEvent.clientX - drag.startX;
        const nextPercent = clampSidebarPercent(
          drag.startPercent - (deltaX / drag.width) * 100,
          drag.width
        );
        setSidebarPercent(nextPercent);
      };

      const endDrag = (endEvent) => {
        if (dividerEl.hasPointerCapture(endEvent.pointerId)) {
          dividerEl.releasePointerCapture(endEvent.pointerId);
        }
        dragRef.current = null;
        setIsResizing(false);
        document.removeEventListener("pointermove", onPointerMove);
        document.removeEventListener("pointerup", endDrag);
        document.removeEventListener("pointercancel", endDrag);
      };

      document.addEventListener("pointermove", onPointerMove);
      document.addEventListener("pointerup", endDrag);
      document.addEventListener("pointercancel", endDrag);
    },
    [enabled, sidebarPercent]
  );

  const listPercent = 100 - sidebarPercent;
  const splitStyle = enabled
    ? {
        "--action-items-split-list": `${listPercent}%`,
        "--action-items-split-sidebar": `${sidebarPercent}%`,
      }
    : undefined;

  return {
    layoutRef,
    splitStyle,
    isResizing,
    sidebarPercent,
    onDividerPointerDown,
  };
};
